import { z } from 'zod'
import { parseInputValue } from '@/lib/utils'

export type ClipboardParsedItem = {
  id: number
  originalValue: string
  validatedValue: string
  error: string | null
  parsedAt: string // ISO 8601 date string
}

const VALIDATE_FNS = {
  numeric: (input: string) => {
    const schema = z
      .string()
      .transform(parseInputValue)
      .refine(val => !Number.isNaN(Number(val)), {
        message: `invalid_numeric`
      })
    const result = schema.safeParse(input)
    return {
      success: result.success,
      value: result.success ? result.data : ``,
      error: result.success ? null : `invalid_numeric`
    }
  },
  percentage: (input: string) => {
    const schema = z
      .string()
      .transform(parseInputValue)
      .refine(val => !Number.isNaN(Number(val)), {
        message: `invalid_numeric`
      })
      .refine(val => Number(val) >= 0 && Number(val) <= 100, {
        message: `invalid_percentage`
      })

    const result = schema.safeParse(input)
    return {
      success: result.success,
      value: result.success ? result.data : ``,
      error: result.success ? null : result.error.errors.map(e => e.message).join(`|`)
    }
  },
  positiveNumeric: (input: string) => {
    const schema = z
      .string()
      .transform(parseInputValue)
      .refine(val => !Number.isNaN(Number(val)), {
        message: `invalid_numeric`
      })
      .refine(val => Number(val) >= 0, {
        message: `invalid_positive`
      })

    const result = schema.safeParse(input)
    return {
      success: result.success,
      value: result.success ? result.data : ``,
      error: result.success ? null : result.error.errors.map(e => e.message).join(`|`)
    }
  }
}

export type ClipboardParseValidationType = keyof typeof VALIDATE_FNS

export type ClipboardParseOptions = {
  validate:
    | ClipboardParseValidationType
    | ((input: string) => {
        success: boolean
        value: string
        error: string | null
      })
}

export type ClipboardParseItemError = {
  itemId: number
  itemValue: string
  code: string
}

export type ClipboardParseResult = {
  clipboardContent: string
  data: ClipboardParsedItem[]
  errors: ClipboardParseItemError[]
  parsedAt: string
}

export function parseExcelData(pastedData: string, options: ClipboardParseOptions): ClipboardParseResult {
  const rows = pastedData.split(/\r\n|\r|\n/) // Handle different line breaks
  const parsedData: ClipboardParsedItem[] = []

  const timestamp = new Date().toISOString()
  const itemErrors: ClipboardParseItemError[] = []

  // Remove trailing newline if it exists (Windows Excel behavior)
  if (rows.length > 0 && rows[rows.length - 1] === ``) {
    rows.pop()
  }

  const firstRow = rows[0] ?? ``

  const cells = firstRow.split(`\t`)

  cells.forEach((cell, index) => {
    const trimmedCell = cell.trim() // Handle empty cells and whitespace

    const { success, error, value } =
      typeof options.validate === `function` ? options.validate(trimmedCell) : VALIDATE_FNS[options.validate](trimmedCell)
    const itemErrorCode = success ? null : error
    parsedData.push({
      id: index,
      originalValue: trimmedCell,
      validatedValue: value,
      error: itemErrorCode,
      parsedAt: timestamp
    })
    if (itemErrorCode) {
      itemErrors.push({ code: itemErrorCode, itemId: index, itemValue: cell })
    }
  })

  return {
    clipboardContent: pastedData,
    data: parsedData,
    errors: itemErrors,
    parsedAt: timestamp
  }
}

export async function readClipboardData(): Promise<string> {
  try {
    const text = await navigator.clipboard.readText()
    return text
  } catch (error) {
    console.error(`Failed to read clipboard contents: `, error)
    return ``
  }
}
